import { useRouter } from "next/router";
import { Trans } from "react-i18next";
import { DEFAULT_ROWS, ROWS_PER_PAGE_OPTIONS } from "../../../constants";
import {
    DataTableSettings,
    UpdateDataTableSettingsFunction,
} from "../../../models/table.model";
import { isNumberValid } from "../../../utils";

const usePaginatorProps = () => {
    const paginatorProps = {
        // props from https://primereact.org/datatable/ which are relatable to paginator
        paginator: true,
        rows: DEFAULT_ROWS,
        rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
        paginatorClassName: "justify-end",
        paginatorTemplate: {
            layout: "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
            CurrentPageReport: (options: {
                className: string;
                first: number;
                last: number;
                totalRecords: number;
            }) => {
                const {
                    className: CurrentPageReportClassNames,
                    first,
                    last,
                    totalRecords,
                } = options;

                return (
                    <span className={CurrentPageReportClassNames}>
                        <Trans
                            i18nKey="common:pagination"
                            values={{
                                first,
                                last,
                                totalRecords,
                            }}
                        />
                    </span>
                );
            },
        },
    };

    return paginatorProps;
};

interface NewPaginatiorProps {
    onPageSettingsChanged?: UpdateDataTableSettingsFunction;
    tablePageSettings?: DataTableSettings;
    itemCount?: number;
    rowsPerPageOptions?: number[];
}

const useNewPaginatiorProps = ({
    onPageSettingsChanged,
    tablePageSettings,
    itemCount = 0,
    rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
}: NewPaginatiorProps) => {
    const { page = 1, pageSize = DEFAULT_ROWS } = tablePageSettings || {};

    const paginatorProps = {
        // props from https://primereact.org/datatable/ which are relatable to paginator
        lazy: true,
        paginator: true,
        totalRecords: itemCount,
        first: page * pageSize - pageSize, // index of the first item of the current page
        rows: pageSize,
        rowsPerPageOptions,
        paginatorClassName: "justify-end",
        onPage: (newPageSettings: any, newPageSettings2: any) => {
            if (!onPageSettingsChanged) {
                return;
            }

            onPageSettingsChanged((oldSettings: DataTableSettings) => {
                const newSettings = {
                    ...oldSettings,
                    pageSize: newPageSettings.rows,
                    page: newPageSettings.page + 1,
                } as DataTableSettings;

                if (newSettings.page || newSettings.pageSize) {
                    const url = new URL(window.location.href);
                    if (newSettings.page) {
                        url.searchParams.set(
                            "page",
                            newSettings.page.toString()
                        );
                    }
                    if (newSettings.pageSize) {
                        url.searchParams.set(
                            "pageSize",
                            newSettings.pageSize.toString()
                        );
                    }
                    window.history.replaceState(null, "", url.toString());
                }

                return newSettings;
            });
        },
        paginatorTemplate: {
            layout: "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
            CurrentPageReport: (options: {
                className: string;
                first: number;
                last: number;
                totalRecords: number;
            }) => {
                const {
                    className: CurrentPageReportClassNames,
                    first,
                    last,
                    totalRecords,
                    ...restReport
                } = options;

                return (
                    <span className={CurrentPageReportClassNames}>
                        <Trans
                            i18nKey="common:pagination"
                            values={{
                                first,
                                last,
                                totalRecords,
                            }}
                        />
                    </span>
                );
            },
        },
    };

    return paginatorProps;
};

const usePaginatorPropsSwitch = (...args: any[]) => {
    const isOldPagination = isNumberValid(args[0]); // Old pagination hook expects a single number (data length)

    // Hooks must be called unconditionally (Rules of Hooks)
    const paginatorProps = usePaginatorProps();
    const newPaginatorProps = useNewPaginatiorProps(args[0]);

    /*  
        the router needs to be here and not in the new paginator because of "react-condition-error"
        react hooks need to be to render in exact same conditions and order
    */
    const router = useRouter();
    const { page, pageSize } = router.query as {
        page?: number;
        pageSize?: number;
    };
    const newPaginatorPropsWithQueryParams = {
        page,
        pageSize,
        ...newPaginatorProps,
    } as NewPaginatiorProps;

    return isOldPagination ? paginatorProps : newPaginatorPropsWithQueryParams;
};

export default usePaginatorPropsSwitch;
